import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectedUnit } from '../redux/reducers/unitSlice';
import { selectedTargetGroup } from '../redux/reducers/targetGroupSlice';
import { useStudentSkillStates } from './useStudentSkillStates';
import { Unit } from '../models/Unit';
import { isUnitComplete } from '../businessLogicService/skillCompletionUtils';

export const useDefaultUnit = (providedUnits?: Unit[]): Unit | null => {
  const unitRedux = useAppSelector(selectedUnit);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);

  // Get student skill states for the current target group
  const { studentSkillStates } = useStudentSkillStates({
    filterByTargetGroup: true,
    includeExtensions: false,
  });

  return useMemo(() => {
    if (!targetGroupRedux) return null;

    // Get all units from the current target group
    let targetGroupUnits: Unit[] = [];

    if (providedUnits && providedUnits.length > 0) {
      // Use provided units if available
      targetGroupUnits = providedUnits.filter(unit => unit.targetGroup?.id === targetGroupRedux.id);
    } else {
      // Fallback: derive units from skill states
      const unitsFromSkills = new Map<string, Unit>();

      studentSkillStates.forEach(skillState => {
        skillState.skill?.targetSkills?.forEach(targetSkill => {
          if (targetSkill.target?.targetGroup?.id === targetGroupRedux.id) {
            const unitId = targetSkill.target.id;
            if (!unitsFromSkills.has(unitId)) {
              unitsFromSkills.set(unitId, {
                id: unitId,
                name: targetSkill.target.name || '',
                summary: targetSkill.target.name || '',
                targetGroup: targetGroupRedux,
              });
            }
          }
        });
      });

      targetGroupUnits = Array.from(unitsFromSkills.values());
    }

    if (targetGroupUnits.length === 0) return null;

    // // Compute a list of skills by recency of completion (most recent first).
    // const skillsByRecency = [...studentSkillStates]
    //   .filter(skill => (skill.modifiedOn || skill.createdOn) && isSkillCompleted(skill))
    //   .sort((a, b) => {
    //     const dateA = new Date(a.modifiedOn || a.createdOn || '');
    //     const dateB = new Date(b.modifiedOn || b.createdOn || '');
    //     return dateB.getTime() - dateA.getTime();
    //   });

    // Compute a list of units whose target skills are not all complete, ordered by unit name.
    const incompleteUnits = targetGroupUnits
      .filter(unit => !isUnitComplete(studentSkillStates, unit.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    // // Iterate over the list of recently completed skills.
    // // Filter the list of units that contain it to only contain the incomplete ones.
    // // If the list is non-empty, return the current unit, if it is present, otherwise return the first unit when ordered by name.
    // for (const skill of skillsByRecency) {
    //   const unitsContainingSkill = incompleteUnits.filter(
    //     unit => skill.skill?.targetSkills?.some(targetSkill => targetSkill.target?.id === unit.id),
    //   );

    //   if (unitsContainingSkill.length > 0) {
    //     // If current unit is in the list, return it
    //     if (unitRedux && unitsContainingSkill.some(unit => unit.id === unitRedux.id)) {
    //       return unitRedux;
    //     }
    //     // Otherwise return the first unit when ordered by name
    //     return unitsContainingSkill[0];
    //   }
    // }

    // If no unit has been returned, return the first unit that is incomplete.
    if (incompleteUnits.length > 0) {
      return incompleteUnits[0];
    }

    // If still no unit has been returned, return the first unit.
    return targetGroupUnits[0];
  }, [studentSkillStates, targetGroupRedux, unitRedux, providedUnits]);
};
